import { pathOr } from 'ramda';

import type { HeaderType } from '@frontend/api';

import type { RootState } from 'store/configureStore';

import {
  getComponentById,
  getComponentByIdDenormalized,
  getPart,
} from '../selectors';

export const getSourceComponentByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(null, ['sourceComponent'], getComponentById(state, componentId));

export const getSourceErpHeaderTypeByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr<HeaderType | null>(
    null,
    ['sourceErpHeaderType'],
    getComponentById(state, componentId),
  );

export const getSourceErpHeaderCodeByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr<string | null>(
    null,
    ['sourceErpHeaderCode'],
    getComponentById(state, componentId),
  );

export const getSourceTypeByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(null, ['sourceType'], getComponentById(state, componentId));

export const getPartUuidByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr<string | null>(null, ['part'], getComponentById(state, componentId));

export const getDfmByComponentId = (state: RootState, componentId: number) =>
  pathOr(
    null,
    ['dfm'],
    getPart(state, getPartUuidByComponentId(state, componentId)),
  );

export const getFilenameByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr(
    null,
    ['filename'],
    getPart(state, getPartUuidByComponentId(state, componentId)),
  );

export const getFileTypeByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr(
    null,
    ['fileType'],
    getPart(state, getPartUuidByComponentId(state, componentId)),
  );

export const getContainsVirusByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr(
    null,
    ['containsVirus'],
    getPart(state, getPartUuidByComponentId(state, componentId)),
  );

export const getMaterialByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(null, ['material'], getComponentById(state, componentId));

export const getOperationsByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr([], ['operations'], getComponentByIdDenormalized(state, componentId));

export const getFinishesByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr([], ['finishes'], getComponentByIdDenormalized(state, componentId));

export const getPartNumberByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(null, ['partNumber'], getComponentById(state, componentId));

export const getProcessByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr([], ['process'], getComponentById(state, componentId));

export const getQuantitiesByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr([], ['quantities'], getComponentByIdDenormalized(state, componentId));

export const getComponentHasPricing = (state: RootState, componentId: number) =>
  getQuantitiesByComponentId(state, componentId).some(
    (quantity) => parseFloat(quantity.totalPriceWithAllAddOns) !== 0,
  );

export const getHasNon100PctYield = (state: RootState, componentId: number) =>
  getQuantitiesByComponentId(state, componentId).some(
    (qty) =>
      (qty.manualYieldPct && qty.manualYieldPct !== 100) ||
      (qty.manualYieldPct === null && qty.yieldPct !== 100),
  );

export const getRevisionByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(null, ['revision'], getComponentById(state, componentId));

export const getDescriptionByComponentId = (
  state: RootState,
  componentId: number,
) => getComponentById(state, componentId)?.description ?? null;

export const getTypeByComponentId = (state: RootState, componentId: number) =>
  pathOr(null, ['type'], getComponentById(state, componentId));

export const getIsAssemblyByComponentId = (
  state: RootState,
  componentId: number,
) => {
  return pathOr<boolean | null>(
    null,
    ['isAssembly'],
    getComponentById(state, componentId),
  );
};

export const getObtainMethodByComponentId = (
  state: RootState,
  componentId: number,
) => {
  return pathOr<string>(
    '',
    ['obtainMethod'],
    getComponentById(state, componentId),
  );
};

export const getPrimaryFileUuidByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr(
    null,
    ['primaryFileUuid'],
    getPart(state, getPartUuidByComponentId(state, componentId)),
  );

export const getIsRootComponentByComponentId = (
  state: RootState,
  componentId: number,
) => pathOr(false, ['isRootComponent'], getComponentById(state, componentId));

export const getWorkflowStatusByComponentId = (
  state: RootState,
  componentId: number,
) =>
  pathOr(undefined, ['workflowStatus'], getComponentById(state, componentId));

export const getHasChildrenByComponentId = (
  state: RootState,
  componentId: number,
) => getComponentById(state, componentId)?.hasChildren || false;

export const getHasProcessByComponentId = (
  state: RootState,
  componentId: number,
) => getComponentById(state, componentId)?.process != null;

export const getProcessingFailedByComponentId = (
  state: RootState,
  componentId: number,
) =>
  getPart(state, getPartUuidByComponentId(state, componentId))
    ?.processingFailed;

export const getHasFailedChildrenByComponentId = (
  state: RootState,
  componentId: number,
) =>
  getPart(state, getPartUuidByComponentId(state, componentId))
    ?.hasFailedChildren;
